import {ReactElement, SyntheticEvent, useEffect, useState} from 'react';
import {Box, Stack, Typography, useTheme} from '@mui/material';
import {TreeView} from '@mui/x-tree-view';
import {TreeItem} from '@mui/x-tree-view/TreeItem';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import {useTranslation} from 'react-i18next';
import {useSnackbar} from 'notistack';

import {
  DicomContainer,
  MainContainer,
  MainNoInfoContainer,
  MainTreeViewContainer,
  TreeViewContainer
} from './DicomComponentStyles';
import {LoadingScreen} from 'src/minimal-components/loading-screen';
import FileThumbnail from 'src/minimal-components/file-thumbnail/file-thumbnail';
import EncryptUtils from 'src/utils/EncryptUtils';
import {getErrorMessage} from '@shared/utils/ErrorUtils';
import {getDicomStudiesData} from '@shared/services/service-handlers/PrivateService';
import {environment} from '@shared/config/environment';
import EmptyContent from '@components/empty-content/empty-content';
import SnackBarConfig from '@components/snackbar/SnackbarConfig';
import ComponentLoader from '@components/loaders/component-loader/ComponentLoader';

interface DicomProps {
  subModuleUIData: any;
  patient_id: string | any;
  height?: any;
}

const DicomComponent = (props: DicomProps): ReactElement => {
  const theme = useTheme();
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const {baseUrl, dicomVM} = environment;
  const {subModuleUIData, height} = props;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [iframeSrc, setIframeSrc] = useState<string | null>('');
  const [folders, setFolders] = useState<any[]>([]);
  const [studies, setStudies] = useState<Record<string, any[]>>({});
  const [isNodeToggled, setIsNodeToggle] = useState<Record<string, boolean>>(
    {}
  );
  const [loadingStudies, setLoadingStudies] = useState<Record<string, boolean>>(
    {}
  );

  const fetchFolders = async () => {
    setIsLoading(true);
    try {
      const data: any = {
        patient_id: props?.patient_id,
        sql_data: subModuleUIData
      };

      const encryptRequestData = EncryptUtils.encryptedData(data);
      const response = await getDicomStudiesData({tk: encryptRequestData});

      setFolders(response?.data || []);
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      enqueueSnackbar(getErrorMessage(e), SnackBarConfig.getError());
    }
  };

  useEffect(() => {
    fetchFolders();
  }, []);

  const fetchStudiesForFolder = async (folderName: string) => {
    if (studies[folderName]) return;
    setLoadingStudies(prev => ({...prev, [folderName]: true}));
    try {
      const data: any = {
        patient_id: props?.patient_id,
        legacy_app: folderName,
        sql_data: subModuleUIData
      };

      const encryptRequestData = EncryptUtils.encryptedData(data);
      const response = await getDicomStudiesData({tk: encryptRequestData});

      setStudies(prev => ({
        ...prev,
        [folderName]: response?.data || []
      }));
      setLoadingStudies(prev => ({...prev, [folderName]: false}));
    } catch (e) {
      setLoadingStudies(prev => ({...prev, [folderName]: false}));
      enqueueSnackbar(getErrorMessage(e), SnackBarConfig.getError());
    }
  };

  const handleFolderChange = async (event: SyntheticEvent, nodes: string[]) => {
    const openedNodes: Record<string, boolean> = {};
    nodes.forEach(node => {
      openedNodes[node] = true;
    });

    setIsNodeToggle(openedNodes);

    nodes.forEach(folderName => {
      if (!studies[folderName]) {
        fetchStudiesForFolder(folderName);
      }
    });
  };

  const handleFileClick = (StudyInstanceUID: any) => {
    setIframeSrc(
      `${dicomVM}?url=${baseUrl}/dicom/studies/json?tk=${StudyInstanceUID}`
    );
  };

  const setWidth = () => {
    return subModuleUIData.layout.browser.grid_columns;
  };

  const getLoaderComponent = (): JSX.Element => (
    <MainNoInfoContainer height={height}>
      <LoadingScreen />
    </MainNoInfoContainer>
  );

  const renderTreeItem = (folder: any) => {
    return (
      <TreeItem
        nodeId={folder.LegacyApp}
        label={
          <Stack direction="row" alignItems="center" spacing={1} my={0.5}>
            <FileThumbnail
              file={isNodeToggled[folder.LegacyApp] ? 'openfolder' : 'folder'}
              sx={{width: 15, height: 15}}
            />
            <Typography
              noWrap
              variant="inherit"
              sx={{
                cursor: 'pointer',
                textOverflow: 'clip',
                overflow: 'unset',
                paddingRight: '1vh',
                color: theme.palette.common.black,
                ...(isNodeToggled[folder.LegacyApp] && {
                  fontWeight: 'bold'
                })
              }}>
              {folder.LegacyApp || 'Studies'}
            </Typography>
          </Stack>
        }>
        {loadingStudies[folder.LegacyApp] ? (
          <ComponentLoader />
        ) : studies[folder.LegacyApp] &&
          studies[folder.LegacyApp].length > 0 ? (
          (studies[folder.LegacyApp] || []).map(study => (
            <TreeItem
              key={study.StudyInstanceUID}
              nodeId={study.StudyInstanceUID}
              label={
                <div onClick={() => handleFileClick(study?.StudyInstanceUID)}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    my={0.5}>
                    <FileThumbnail
                      file={'dcm'}
                      tooltip={true}
                      sx={{width: 15, height: 15}}
                    />

                    <Typography
                      noWrap
                      variant="inherit"
                      sx={{
                        cursor: 'pointer',
                        textOverflow: 'clip',
                        overflow: 'unset',
                        paddingRight: '1vh',
                        color: theme.palette.common.black
                      }}>
                      {study?.StudyInfo
                        ? study.StudyInfo.replace(/^[,:\s]+|[,:\s]+$/g, '')
                        : 'Series'}
                    </Typography>
                  </Stack>
                </div>
              }
            />
          ))
        ) : (
          <TreeItem
            nodeId={`${folder.LegacyApp}-no-studies`}
            label={
              <Typography variant="body2" color="textSecondary" mt={1}>
                No Studies Found
              </Typography>
            }
          />
        )}
      </TreeItem>
    );
  };

  const getTreeComponent = (): JSX.Element => (
    <MainTreeViewContainer item md={setWidth()} xs={8}>
      <TreeViewContainer
        isdochidden={false}
        shoulddisplaymetainfo={`false`}
        height={height}>
        {folders?.map((folder: any) => (
          <TreeView
            key={folder.LegacyApp}
            aria-label="file system navigator"
            defaultCollapseIcon={
              <ArrowDropDownIcon style={{color: theme.palette.text.disabled}} />
            }
            defaultExpandIcon={
              <ArrowRightIcon style={{color: theme.palette.text.disabled}} />
            }
            defaultEndIcon={''}
            onNodeToggle={handleFolderChange}>
            {renderTreeItem(folder)}
          </TreeView>
        ))}
      </TreeViewContainer>
    </MainTreeViewContainer>
  );

  const getDicomViewerComponent = (): JSX.Element => {
    return iframeSrc ? (
      <Box mt={0.1}>
        <iframe
          src={iframeSrc}
          title={iframeSrc}
          style={{
            width: '54vw',
            height: '80vh',
            borderRadius: '8px',
            borderColor: '#f4f6f8',
            border: '0px'
          }}
        />
      </Box>
    ) : (
      <EmptyContent
        title="No file or folder selected"
        description="Select a file or folder to read"
        imgUrl="/assets/icons/empty/ic_email_selected.svg"
        sx={{
          borderRadius: 1.5,
          bgcolor: 'background.default'
        }}
      />
    );
  };

  const getTreeAndDicomViewerComponent = (): JSX.Element => {
    if (!folders.length) {
      return (
        <MainNoInfoContainer height={height}>
          <EmptyContent title={t('PRIVATE.DICOM.NO_DATA', {ns: 'private'})} />
        </MainNoInfoContainer>
      );
    }

    return (
      <MainContainer height={height}>
        {getTreeComponent()}
        {getDicomViewerComponent()}
      </MainContainer>
    );
  };

  return (
    <DicomContainer>
      {isLoading ? getLoaderComponent() : getTreeAndDicomViewerComponent()}
    </DicomContainer>
  );
};

export default DicomComponent;

